import { z } from 'zod';
import { isoTimestamp } from './common';
import { modelIdSchema } from './providers';
import { resourceNameSchema } from './tools';

/**
 * - `llm_judge`: a model grades the run against your instructions (needs a provider and model);
 * - `contains` / `regex`: the answer includes a text / matches an RE2 pattern;
 * - `json_valid`: the answer is valid JSON;
 * - `length`: the answer's length is within bounds;
 * - `tool_called`: the run called a given tool.
 *
 * Every score is normalized to 0–1.
 */
export const SCORER_TYPES = [
  'llm_judge',
  'contains',
  'regex',
  'json_valid',
  'length',
  'tool_called',
] as const;
export const scorerTypeSchema = z.enum(SCORER_TYPES);
export type ScorerType = z.infer<typeof scorerTypeSchema>;

export const llmJudgeConfigSchema = z
  .object({
    instructions: z.string().trim().min(1, 'Tell the judge what to grade').max(10_000),
    scale: z
      .object({ min: z.number().int().min(0).max(100), max: z.number().int().min(1).max(100) })
      .refine((s) => s.max > s.min, { message: 'max must be greater than min' })
      .default({ min: 0, max: 10 }),
  })
  .strict();
export const containsConfigSchema = z
  .object({
    text: z.string().min(1).max(1000),
    caseSensitive: z.boolean().default(false),
    negate: z.boolean().default(false),
  })
  .strict();
export const regexConfigSchema = z
  .object({ pattern: z.string().min(1).max(1000), negate: z.boolean().default(false) })
  .strict();
export const jsonValidConfigSchema = z.object({}).strict();
export const lengthConfigSchema = z
  .object({
    min: z.number().int().min(0).max(1_000_000).optional(),
    max: z.number().int().min(1).max(1_000_000).optional(),
  })
  .strict()
  .refine((c) => c.min !== undefined || c.max !== undefined, { message: 'Set min, max or both' });
export const toolCalledConfigSchema = z.object({ toolName: z.string().min(1).max(128) }).strict();

const scorerCommon = {
  name: resourceNameSchema,
  description: z.string().trim().max(2000).default(''),
  /** Agents whose finished runs are scored automatically. */
  agentIds: z.array(z.uuid()).max(50).default([]),
  /** Share of those runs to score (1 = every run). */
  samplingRate: z.number().min(0).max(1).default(1),
  enabled: z.boolean().default(true),
};

export const createScorerRequestSchema = z.discriminatedUnion('type', [
  z.object({
    ...scorerCommon,
    type: z.literal('llm_judge'),
    config: llmJudgeConfigSchema,
    providerConnectionId: z.uuid(),
    model: modelIdSchema,
  }),
  z.object({ ...scorerCommon, type: z.literal('contains'), config: containsConfigSchema }),
  z.object({ ...scorerCommon, type: z.literal('regex'), config: regexConfigSchema }),
  z.object({
    ...scorerCommon,
    type: z.literal('json_valid'),
    config: jsonValidConfigSchema.default({}),
  }),
  z.object({ ...scorerCommon, type: z.literal('length'), config: lengthConfigSchema }),
  z.object({ ...scorerCommon, type: z.literal('tool_called'), config: toolCalledConfigSchema }),
]);
export type CreateScorerRequest = z.infer<typeof createScorerRequestSchema>;

export type LlmJudgeConfig = z.infer<typeof llmJudgeConfigSchema>;
export type ContainsConfig = z.infer<typeof containsConfigSchema>;
export type RegexConfig = z.infer<typeof regexConfigSchema>;
export type LengthConfig = z.infer<typeof lengthConfigSchema>;
export type ToolCalledConfig = z.infer<typeof toolCalledConfigSchema>;
export type ScorerConfig =
  | LlmJudgeConfig
  | ContainsConfig
  | RegexConfig
  | z.infer<typeof jsonValidConfigSchema>
  | LengthConfig
  | ToolCalledConfig;

export const SCORER_CONFIG_SCHEMAS = {
  llm_judge: llmJudgeConfigSchema,
  contains: containsConfigSchema,
  regex: regexConfigSchema,
  json_valid: jsonValidConfigSchema,
  length: lengthConfigSchema,
  tool_called: toolCalledConfigSchema,
} as const;

export const updateScorerRequestSchema = z
  .object({
    description: z.string().trim().max(2000).optional(),
    /** Validated against the scorer's type by the service. */
    config: z.record(z.string(), z.unknown()).optional(),
    agentIds: z.array(z.uuid()).max(50).optional(),
    samplingRate: z.number().min(0).max(1).optional(),
    enabled: z.boolean().optional(),
    providerConnectionId: z.uuid().optional(),
    model: modelIdSchema.optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
export type UpdateScorerRequest = z.infer<typeof updateScorerRequestSchema>;

export const scorerDtoSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string(),
  type: scorerTypeSchema,
  config: z.record(z.string(), z.unknown()),
  providerConnectionId: z.uuid().nullable(),
  model: z.string().nullable(),
  agentIds: z.array(z.uuid()),
  samplingRate: z.number(),
  enabled: z.boolean(),
  stats: z.object({ count: z.number().int(), average: z.number().nullable() }),
  createdAt: isoTimestamp,
  updatedAt: isoTimestamp,
});
export type ScorerDto = z.infer<typeof scorerDtoSchema>;

export const scoreSourceSchema = z.enum(['auto', 'manual']);
export type ScoreSource = z.infer<typeof scoreSourceSchema>;

export const scoreDtoSchema = z.object({
  id: z.uuid(),
  scorerId: z.uuid(),
  scorerName: z.string(),
  runId: z.uuid(),
  agentId: z.uuid(),
  /** 0–1. */
  score: z.number(),
  reason: z.string().nullable(),
  source: scoreSourceSchema,
  createdAt: isoTimestamp,
});
export type ScoreDto = z.infer<typeof scoreDtoSchema>;

export const scoreListQuerySchema = z.object({
  cursor: z.uuid().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  scorerId: z.uuid().optional(),
  agentId: z.uuid().optional(),
  runId: z.uuid().optional(),
});
export type ScoreListQuery = z.infer<typeof scoreListQuerySchema>;

export const scoreRunRequestSchema = z.object({ scorerId: z.uuid() });

export const backfillScoresRequestSchema = z.object({
  /** The most recent finished runs to score. */
  limit: z.number().int().min(1).max(50).default(20),
  agentId: z.uuid().optional(),
});
export type BackfillScoresRequest = z.infer<typeof backfillScoresRequestSchema>;

export const backfillScoresResultSchema = z.object({
  scored: z.number().int(),
  failed: z.number().int(),
});
